-- | What a conjugation table is made of. The table itself is generated into
-- | `Flashcards.Data.Verbs.Spanish` from `data/es-verbs.csv`; this is where
-- | its types live, since a generated module cannot be where they are defined.
module Flashcards.Verbs.Table
  ( Cell
  , Deviation
  , Person(..)
  , Tense(..)
  , formOf
  , pronoun
  )
  where

import Prelude

import Data.Array as Array
import Data.Maybe (Maybe)

-- | The four that carry the difficulty. Future and conditional are regular
-- | for almost every verb and are better added later than padded in now.
data Tense
  = Present
  | Preterite
  | Imperfect
  | Subjunctive

derive instance Eq Tense
derive instance Ord Tense

instance Show Tense where
  show Present = "Present"
  show Preterite = "Preterite"
  show Imperfect = "Imperfect"
  show Subjunctive = "Subjunctive"

-- | Five, not six: no *vosotros*, since the deck prefers es-MX. `Sg3` and
-- | `Pl3` carry *usted* and *ustedes*.
data Person
  = Sg1
  | Sg2
  | Sg3
  | Pl1
  | Pl3

derive instance Eq Person
derive instance Ord Person

instance Show Person where
  show Sg1 = "Sg1"
  show Sg2 = "Sg2"
  show Sg3 = "Sg3"
  show Pl1 = "Pl1"
  show Pl3 = "Pl3"

-- | The person as a prompt names it: the person shift's `→ nosotros`, and
-- | the subject an error correction puts in front of a sentence that has
-- | none. One pronoun for each, though `Sg3` and `Pl3` also carry *ella*,
-- | *usted* and the rest: the form is the same, and a list would be read as
-- | a choice to make.
pronoun :: Person -> String
pronoun = case _ of
  Sg1 -> "yo"
  Sg2 -> "tú"
  Sg3 -> "él"
  Pl1 -> "nosotros"
  Pl3 -> "ellos"

type Cell =
  { infinitive :: String
  , tense :: Tense
  , person :: Person
  -- | Accented exactly. Grading may forgive a missing accent; the table
  -- | does not have one to forgive.
  , form :: String
  }

-- | A cell whose form is not what the regular pattern gives, and what the
-- | pattern would have given instead: `tener`, `Preterite`, `Sg1`, `tení`,
-- | where the table has `tuve`. Generated by the same `regular` that
-- | `check-verbs` prints its review with, so the two cannot disagree.
type Deviation =
  { infinitive :: String
  , tense :: Tense
  , person :: Person
  , regular :: String
  }

-- | The form in one cell, if the table has that verb.
-- |
-- | A scan, not an index like `Flashcards.Deck`'s. The deck's is queried on
-- | every grade and every render; this runs while the drills' `items` are
-- | built and never after, and an index is for lookups that repeat. See #20.
formOf :: String -> Tense -> Person -> Array Cell -> Maybe String
formOf infinitive tense person =
  map _.form <<< Array.find \c -> c.infinitive == infinitive && c.tense == tense && c.person == person
